/**
 * Loads all game assets (spritesheets + sounds) and provides global access.
 *
 * Asset URLs assume Vite's publicDir is set to 'assets/',
 * so e.g. assets/PNG/Tiles/Tilemap/tilemap_packed.png → /PNG/Tiles/Tilemap/tilemap_packed.png
 */

import { SpriteSheet } from './SpriteSheet';

/* ── Image loading helper ── */
function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
    img.src = url;
  });
}

/* ── Audio loading helper ── */
function loadAudio(url: string): Promise<AudioBuffer | null> {
  // We'll use HTMLAudioElement for broader compatibility
  return new Promise((resolve) => {
    const audio = new Audio();
    audio.oncanplaythrough = () => resolve(null); // just warm up cache
    audio.onerror = () => resolve(null); // non-fatal
    audio.src = url;
    audio.load();
    resolve(null);
  });
}

/* ── Public interface ── */
export interface GameAssets {
  tiles: SpriteSheet;
  players: SpriteSheet;
  enemies: SpriteSheet;
  ui: SpriteSheet;
  weapons: SpriteSheet;
  sounds: Map<string, string>;  // name → URL
}

/** Singleton asset store. `null` until `loadAssets()` completes. */
let _assets: GameAssets | null = null;

export function getAssets(): GameAssets | null {
  return _assets;
}

/** Sound file URLs keyed by a short name. */
const SOUND_FILES: Record<string, string> = {
  'coin-a': '/Sounds/coin-a.ogg',
  'coin-b': '/Sounds/coin-b.ogg',
  'coin-c': '/Sounds/coin-c.ogg',
  'coin-d': '/Sounds/coin-d.ogg',
  'select-a': '/Sounds/select-a.ogg',
  'move-a': '/Sounds/move-a.ogg',
  'move-b': '/Sounds/move-b.ogg',
  'move-c': '/Sounds/move-c.ogg',
  'move-d': '/Sounds/move-d.ogg',
  'jump-a': '/Sounds/jump-a.ogg',
  'jump-b': '/Sounds/jump-b.ogg',
  'jump-c': '/Sounds/jump-c.ogg',
  'jump-d': '/Sounds/jump-d.ogg',
  'jump-e': '/Sounds/jump-e.ogg',
  'jump-f': '/Sounds/jump-f.ogg',
  'error-a': '/Sounds/error-a.ogg',
  'error-b': '/Sounds/error-b.ogg',
  'error-c': '/Sounds/error-c.ogg',
  'fall-a': '/Sounds/fall-a.ogg',
  'fall-b': '/Sounds/fall-b.ogg',
  'hurt-a': '/Sounds/hurt-a.ogg',
  'hurt-b': '/Sounds/hurt-b.ogg',
  'hurt-c': '/Sounds/hurt-c.ogg',
  'hurt-d': '/Sounds/hurt-d.ogg',
  'hurt-e': '/Sounds/hurt-e.ogg',
  'lose-a': '/Sounds/lose-a.ogg',
  'lose-b': '/Sounds/lose-b.ogg',
  'lose-c': '/Sounds/lose-c.ogg',
  'lose-d': '/Sounds/lose-d.ogg',
  'explosion-a': '/Sounds/explosion-a.ogg',
  'explosion-b': '/Sounds/explosion-b.ogg',
  'explosion-c': '/Sounds/explosion-c.ogg',
  'shoot-a': '/Sounds/shoot-a.ogg',
  'shoot-b': '/Sounds/shoot-b.ogg',
  'shoot-c': '/Sounds/shoot-c.ogg',
  'shoot-d': '/Sounds/shoot-d.ogg',
  'shoot-e': '/Sounds/shoot-e.ogg',
  'shoot-f': '/Sounds/shoot-f.ogg',
  'shoot-g': '/Sounds/shoot-g.ogg',
  'shoot-h': '/Sounds/shoot-h.ogg',
};

/**
 * Load all game assets. Call once before starting the game.
 * Returns a promise that resolves with the loaded assets.
 */
export async function loadAssets(
  onProgress?: (loaded: number, total: number) => void,
): Promise<GameAssets> {
  const total = 5 + Object.keys(SOUND_FILES).length;
  let loaded = 0;

  const tick = () => {
    loaded++;
    onProgress?.(loaded, total);
  };

  // Load all spritesheets in parallel
  const [tilesImg, playersImg, enemiesImg, uiImg, weaponsImg] = await Promise.all([
    loadImage('/PNG/Tiles/Tilemap/tilemap_packed.png').then((img) => { tick(); return img; }),
    loadImage('/PNG/Players/Tilemap/tilemap_packed.png').then((img) => { tick(); return img; }),
    loadImage('/PNG/Enemies/Tilemap/tilemap_packed.png').then((img) => { tick(); return img; }),
    loadImage('/PNG/Interface/Tilemap/tilemap_packed.png').then((img) => { tick(); return img; }),
    loadImage('/PNG/Weapons/Tilemap/tilemap_packed.png').then((img) => { tick(); return img; }),
  ]);

  // Warm up audio cache (non-blocking errors)
  const soundMap = new Map<string, string>();
  await Promise.all(
    Object.entries(SOUND_FILES).map(async ([name, url]) => {
      await loadAudio(url);
      soundMap.set(name, url);
      tick();
    }),
  );

  _assets = {
    tiles: new SpriteSheet(tilesImg, 16, 16, 18, 13),    // 234 tiles
    players: new SpriteSheet(playersImg, 24, 24, 4, 4),   // 16 sprites
    enemies: new SpriteSheet(enemiesImg, 24, 24, 4, 4),   // 16 sprites
    ui: new SpriteSheet(uiImg, 16, 16, 18, 11),           // 198 tiles
    weapons: new SpriteSheet(weaponsImg, 24, 24, 10, 4),  // 40 sprites
    sounds: soundMap,
  };

  return _assets;
}
